import { ChangeDetectionStrategy, Component, EventEmitter, Output, effect, input, signal } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';

export interface DiscreetSelectOption {
  value: any;
  viewValue: string;
}

@Component({
  selector: 'ui-discreet-select',
  standalone: true,
  imports: [MatButtonModule, MatMenuModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: [
    `
      /* The host button that triggers the menu */
      .discreet-select-trigger {
        /* Remove all button styling */
        background: transparent;
        border: none;
        padding: 4px 8px;

        /* Font styling from the image */
        font-size: 0.95rem;
        font-weight: 400;
        color: #525f7f; /* Muted text color */
        cursor: pointer;

        /* Layout */
        display: inline-flex;
        align-items: center;
        gap: 2px;

        /* Smooth transition for hover */
        transition: background-color 0.2s ease;
      }

      .discreet-select-trigger:hover:not(:disabled) {
        background-color: rgba(0, 0, 0, 0.04); /* Light gray on hover */
        border-radius: 8px;
      }

      .discreet-select-trigger:disabled {
        color: rgba(0, 0, 0, 0.38);
        cursor: not-allowed;
      }

      .discreet-select-trigger mat-icon {
        font-size: 20px;
        width: 20px;
        height: 20px;
        color: #525f7f; /* Muted arrow color */
      }
    `,
  ],
  template: `
    <button type="button" class="discreet-select-trigger" [matMenuTriggerFor]="menu" [disabled]="disabled()">
      <span>{{ currentViewValue() }}</span>
      <mat-icon>keyboard_arrow_down</mat-icon>
    </button>

    <mat-menu #menu="matMenu">
      @for (option of normalizedOptions(); track option.value) {
        <button mat-menu-item (click)="onSelect(option)">
          <span>{{ option.viewValue }}</span>
        </button>
      }
    </mat-menu>
  `,
})
export class DiscreetSelectComponent {
  /**
   * The currently selected value.
   * Can be pre-set.
   */
  value = input<any>();

  /**
   * The list of options. Can be:
   * 1. An array of strings: ['Owner', 'Editor', 'Viewer']
   * 2. An array of objects: [{ value: 1, viewValue: 'Owner' }]
   */
  options = input<string[] | DiscreetSelectOption[]>([]);

  /**
   * Whether the select is disabled.
   */
  disabled = input(false);

  /**
   * Emits the new *value* whenever a selection is made.
   */
  @Output() valueChange = new EventEmitter<any>();

  // --- Internal Signals ---

  // A signal to hold the currently selected option object
  private selectedOption = signal<DiscreetSelectOption | undefined>(undefined);

  /**
   * Internal computed signal that normalizes the `options` input
   * into the { value, viewValue } format.
   */
  normalizedOptions = signal<DiscreetSelectOption[]>([]);

  /**
   * Internal computed signal that determines the text
   * to display on the button.
   */
  currentViewValue = signal<string>('Select');

  constructor() {
    // Effect to normalize options whenever the input changes
    effect(() => {
      const opts = this.options();
      if (!opts || opts.length === 0) {
        this.normalizedOptions.set([]);
        return;
      }

      // Check if options are strings
      if (typeof opts[0] === 'string') {
        this.normalizedOptions.set((opts as string[]).map((val) => ({ value: val, viewValue: val })));
      } else {
        // Assume they are already DiscreetSelectOption[]
        this.normalizedOptions.set(opts as DiscreetSelectOption[]);
      }

      // After normalizing, update the selected option
      this.updateSelectedOptionFromValue();
    });

    // Effect to update the selected option when the `value` input changes
    effect(() => {
      this.updateSelectedOptionFromValue();
    });

    // Effect to update the display text when the selected option changes
    effect(() => {
      const selected = this.selectedOption();
      this.currentViewValue.set(selected ? selected.viewValue : 'Select');
    });
  }

  /**
   * Finds and sets the selected option based on the current `value()`
   */
  private updateSelectedOptionFromValue() {
    const currentValue = this.value();
    const found = this.normalizedOptions().find((opt) => opt.value === currentValue);
    this.selectedOption.set(found);
  }

  /**
   * Called when a user clicks a menu item.
   */
  onSelect(option: DiscreetSelectOption): void {
    this.selectedOption.set(option);
    this.valueChange.emit(option.value);
  }
}
